import type { BaseUser } from "@models";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Button,
  ButtonGroup,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { ChevronDownIcon } from "lucide-react";
import { useState, type CSSProperties } from "react";
import { formatTiptapSuggestionTime } from "src/components/TiptapEditor/collaboration/tiptapSuggestionAdapter";
import { resolveTiptapVersionCardEditors } from "./version-authoring";

export type SimpleEditorSnapshotVersion = {
  date: number;
  meta?: Record<string, unknown> | null;
  name?: string | null;
  version: number;
};

export type SimpleEditorVersionCardStatus =
  | "Live"
  | "Archived"
  | "Routing"
  | "Editable"
  | null;

export type SimpleEditorVersionCardStatusByVersion = ReadonlyMap<
  number,
  SimpleEditorVersionCardStatus
>;

export type SimpleEditorVersionCardAuthor = {
  id: string | null;
  name: string;
};

export type SimpleEditorVersionCardAuthorByVersion = ReadonlyMap<
  number,
  SimpleEditorVersionCardAuthor
>;

export type SimpleEditorVersionCardRestorableByVersion = ReadonlyMap<
  number,
  boolean | undefined
>;

export type SimpleEditorVersionCardTitleByVersion = ReadonlyMap<number, string>;

export const AUTO_VERSIONING_INTERVAL_SECONDS = 10;

type SnapshotVersioningEditor = {
  commands: {
    disableVersioning: () => boolean;
    enableVersioning: () => boolean;
    fetchVersions: () => boolean;
  };
};

type SnapshotVersioningProvider = {
  setAutoVersioningInterval: (seconds: number) => void;
};

export const configureSnapshotVersioning = ({
  editor,
  isEditable,
  provider,
}: {
  editor: SnapshotVersioningEditor;
  isEditable: boolean;
  provider: SnapshotVersioningProvider;
}) => {
  if (isEditable) {
    provider.setAutoVersioningInterval(AUTO_VERSIONING_INTERVAL_SECONDS);
    editor.commands.enableVersioning();
  } else {
    editor.commands.disableVersioning();
  }

  editor.commands.fetchVersions();
};

const TIPTAP_AUTO_VERSION_NAME = "Version updated in tiptap";
const HIDDEN_TIPTAP_VERSION_NAMES = new Set(
  [TIPTAP_AUTO_VERSION_NAME, "New revision", "Save and approve"].map((name) =>
    name.toLowerCase()
  )
);

const documentLiveChipStyle = {
  backgroundColor: "#dcfce7",
  borderColor: "#22c55e",
  color: "#166534",
} satisfies CSSProperties;

const documentRoutingChipStyle = {
  backgroundColor: "#bbd4ff",
  borderColor: "#7da4ff",
} satisfies CSSProperties;

const documentArchivedChipStyle = {
  backgroundColor: "#f3f4f6",
  borderColor: "#9ca3af",
  color: "#374151",
} satisfies CSSProperties;

const versionCardStatusChipStyleByStatus = {
  Archived: documentArchivedChipStyle,
  Live: documentLiveChipStyle,
  Routing: documentRoutingChipStyle,
} satisfies Record<"Archived" | "Live" | "Routing", CSSProperties>;

const isRelevantVersionCardStatus = (status: SimpleEditorVersionCardStatus) =>
  status === "Archived" || status === "Live" || status === "Routing";

export const getVersionTitle = (version: SimpleEditorSnapshotVersion) => {
  const name = version.name?.trim();

  if (name && !HIDDEN_TIPTAP_VERSION_NAMES.has(name.toLowerCase())) {
    return name;
  }

  return `Edit ${version.version + 1}`;
};

export const formatVersionDate = (value: number) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const getVersionViewingMessage = ({
  date,
  revisionTitle,
  status,
}: {
  date: number;
  revisionTitle?: string;
  status: SimpleEditorVersionCardStatus;
}) => {
  if (status === "Routing") {
    return revisionTitle
      ? `Viewing: Routing ${revisionTitle}`
      : "Viewing: Routing Revision";
  }

  if (revisionTitle) {
    return `Viewing: ${revisionTitle}`;
  }

  const formattedDate = formatVersionDate(date);

  return formattedDate
    ? `Viewing: As of ${formattedDate}`
    : "Viewing: Saved Version";
};

const formatVersionCardDate = (value: number) => {
  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? ""
    : formatTiptapSuggestionTime(date.toISOString());
};

const formatVersionEditors = (editors: SimpleEditorVersionCardAuthor[]) =>
  editors.map((editor) => editor.name).join(", ");
export type VersionCardFilter = "relevant" | "all";

const versionCardFilters: { label: string; value: VersionCardFilter }[] = [
  { label: "Relevant", value: "relevant" },
  { label: "All", value: "all" },
];

const getEmptyVersionsMessage = (filter: VersionCardFilter) => {
  if (filter === "relevant") {
    return "No relevant versions yet";
  }

  return "No versions yet";
};

type VersionCardsProps = {
  versions: SimpleEditorSnapshotVersion[];
  comparison?: {
    sourceVersion: number;
    targetVersion: number | null;
  } | null;
  defaultFilter?: VersionCardFilter;
  filter?: VersionCardFilter;
  headMatchingVersion?: number | null;
  selectedVersion?: number | null;
  versionCardAuthorByVersion?: SimpleEditorVersionCardAuthorByVersion;
  versionCardStatusByVersion?: SimpleEditorVersionCardStatusByVersion;
  versionCardRestorableByVersion?: SimpleEditorVersionCardRestorableByVersion;
  versionCardTitleByVersion?: SimpleEditorVersionCardTitleByVersion;
  users?: BaseUser[];
  onSelectVersion: (version: number) => void;
  onCompareVersion?: (
    version: SimpleEditorSnapshotVersion,
    target?: SimpleEditorSnapshotVersion
  ) => void;
  onFilterChange?: (filter: VersionCardFilter) => void;
  onRestoreVersion: (version: SimpleEditorSnapshotVersion) => void;
};

const versionCardChipSx = {
  flex: "0 0 auto",
  "& .MuiChip-label": { fontWeight: 400 },
};
const EMPTY_VERSION_CARD_STATUS_BY_VERSION =
  new Map() satisfies SimpleEditorVersionCardStatusByVersion;
const EMPTY_VERSION_CARD_RESTORABLE_BY_VERSION =
  new Map() satisfies SimpleEditorVersionCardRestorableByVersion;
const EMPTY_VERSION_CARD_TITLE_BY_VERSION =
  new Map() satisfies SimpleEditorVersionCardTitleByVersion;

type VersionComparisonOption = {
  title: string;
  version: SimpleEditorSnapshotVersion;
};

const VersionComparisonButton = ({
  comparisonOptions,
  isMatchingHead,
  onCompareVersion,
  selectedTargetVersion,
  version,
  versionTitle,
}: {
  comparisonOptions: VersionComparisonOption[];
  isMatchingHead: boolean;
  onCompareVersion: (
    version: SimpleEditorSnapshotVersion,
    target?: SimpleEditorSnapshotVersion
  ) => void;
  selectedTargetVersion: number | null;
  version: SimpleEditorSnapshotVersion;
  versionTitle: string;
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const selectedTarget =
    selectedTargetVersion !== null
      ? (comparisonOptions.find(
          ({ version: target }) => target.version === selectedTargetVersion
        ) ?? null)
      : null;
  const comparisonTargets = comparisonOptions.filter(
    ({ version: target }) => target.version !== version.version
  );
  const compareWith = (target: VersionComparisonOption | null) => {
    setMenuAnchor(null);
    target
      ? onCompareVersion(version, target.version)
      : onCompareVersion(version);
  };

  return (
    <>
      <ButtonGroup
        aria-label={`Comparison actions for ${versionTitle}`}
        color="inherit"
        size="small"
        variant="outlined"
      >
        <Button
          data-version-action="compare"
          data-version-key={String(version.version)}
          disabled={!selectedTarget && isMatchingHead}
          onClick={() => compareWith(selectedTarget)}
          sx={{ textTransform: "none" }}
        >
          Compare with {selectedTarget?.title ?? "current"}
        </Button>
        <Button
          aria-expanded={menuAnchor ? "true" : undefined}
          aria-haspopup="menu"
          aria-label="Choose comparison target"
          data-version-action="compare-target"
          data-version-key={String(version.version)}
          disabled={isMatchingHead && comparisonOptions.length === 0}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{ minWidth: 28, px: 0.5 }}
        >
          <ChevronDownIcon aria-hidden="true" size={14} />
        </Button>
      </ButtonGroup>
      {menuAnchor ? (
        <Menu
          anchorEl={menuAnchor}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          BackdropProps={{
            onClick: (event) => {
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation();
              setMenuAnchor(null);
            },
            onMouseDown: (event) => {
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation();
            },
          }}
          MenuListProps={{ sx: { maxHeight: 200, overflowY: "auto" } }}
          onClose={() => setMenuAnchor(null)}
          open
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          variant="menu"
        >
          <MenuItem
            disabled={isMatchingHead}
            onClick={() => compareWith(null)}
            selected={!selectedTarget}
          >
            Current
          </MenuItem>
          {comparisonTargets.map((target) => (
            <MenuItem
              key={target.version.version}
              onClick={() => compareWith(target)}
              selected={
                selectedTarget?.version.version === target.version.version
              }
            >
              {target.title}
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </>
  );
};

export const VersionCards = ({
  versions,
  comparison = null,
  defaultFilter = "all",
  filter,
  headMatchingVersion = null,
  selectedVersion = null,
  versionCardAuthorByVersion = new Map() satisfies SimpleEditorVersionCardAuthorByVersion,
  versionCardStatusByVersion = EMPTY_VERSION_CARD_STATUS_BY_VERSION,
  versionCardRestorableByVersion = EMPTY_VERSION_CARD_RESTORABLE_BY_VERSION,
  versionCardTitleByVersion = EMPTY_VERSION_CARD_TITLE_BY_VERSION,
  users = [],
  onSelectVersion,
  onCompareVersion,
  onFilterChange,
  onRestoreVersion,
}: VersionCardsProps) => {
  const [uncontrolledFilter, setUncontrolledFilter] =
    useState<VersionCardFilter>(defaultFilter);
  const activeFilter = filter ?? uncontrolledFilter;
  const orderedVersions = versions
    .slice()
    .sort((a, b) => b.version - a.version);
  const usersById = new Map(users.map((user) => [user.id, user]));
  const versionCards = orderedVersions.map((version) => {
    const status = versionCardStatusByVersion.get(version.version) ?? null;
    const title =
      versionCardTitleByVersion.get(version.version) ??
      getVersionTitle(version);

    return { status, title, version };
  });
  const comparisonOptions = versionCards
    .filter(({ version }) => version.version !== headMatchingVersion)
    .sort(
      (a, b) =>
        Number(isRelevantVersionCardStatus(b.status)) -
        Number(isRelevantVersionCardStatus(a.status))
    )
    .map(({ title, version }) => ({ title, version }));
  const hasHiddenActiveVersion = versionCards.some(
    ({ status, version }) =>
      (version.version === selectedVersion ||
        version.version === comparison?.sourceVersion) &&
      !isRelevantVersionCardStatus(status)
  );
  const displayedFilter =
    activeFilter === "relevant" && hasHiddenActiveVersion
      ? "all"
      : activeFilter;
  const filteredVersionCards = versionCards.filter((versionCard) => {
    if (displayedFilter === "relevant") {
      return isRelevantVersionCardStatus(versionCard.status);
    }

    return true;
  });

  return versionCards.length > 0 ? (
    <>
      <ToggleButtonGroup
        aria-label="Filter versions"
        className="mb-3"
        exclusive
        fullWidth
        onChange={(_event, nextFilter: VersionCardFilter | null) => {
          if (nextFilter) {
            if (filter === undefined) {
              setUncontrolledFilter(nextFilter);
            }
            onFilterChange?.(nextFilter);
          }
        }}
        size="small"
        value={displayedFilter}
      >
        {versionCardFilters.map((filter) => (
          <ToggleButton
            key={filter.value}
            sx={{ textTransform: "none", flex: 1 }}
            value={filter.value}
          >
            {filter.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {filteredVersionCards.length > 0 ? (
        <div className="simple-editor-suggestions__list">
          {filteredVersionCards.map(({ status, title, version }) => {
            const isSelected = selectedVersion === version.version;
            const isMatchingHead = headMatchingVersion === version.version;
            const isComparisonSource =
              version.version === comparison?.sourceVersion;
            const isRestorable =
              versionCardRestorableByVersion.get(version.version) !== false;
            const isRestoreButtonDisabled = !isRestorable || isMatchingHead;
            const legacyAuthor = versionCardAuthorByVersion.get(
              version.version
            );
            const resolvedEditors = resolveTiptapVersionCardEditors({
              legacyAuthor,
              usersById,
              version,
            });
            const editorNames = formatVersionEditors(resolvedEditors);
            const editorIds = resolvedEditors
              .flatMap((editor) => (editor.id ? [editor.id] : []))
              .join(",");
            const formattedDate = formatVersionCardDate(version.date);
            const versionDataKey = String(version.version);
            const displayedStatus = status === "Editable" ? null : status;
            const selectButtonLabel = [
              `View ${title}`,
              editorNames ? `edited by ${editorNames}` : null,
              formattedDate ? `from ${formattedDate}` : null,
              displayedStatus ? displayedStatus.toLowerCase() : null,
              "read-only",
            ]
              .filter(Boolean)
              .join(", ");

            return (
              <article
                className="simple-editor-version-card"
                data-compared-selected={isComparisonSource}
                data-selected={isSelected ? true : undefined}
                data-version-editor-ids={editorIds}
                data-version-editor-names={editorNames}
                data-version-key={versionDataKey}
                data-version-status={status ?? ""}
                data-version-title={title}
                key={version.version}
              >
                <button
                  aria-label={selectButtonLabel}
                  aria-pressed={isSelected ? true : undefined}
                  className="simple-editor-version-card__select"
                  data-version-action="select"
                  data-version-key={versionDataKey}
                  onClick={() => onSelectVersion(version.version)}
                  type="button"
                />
                <div className="simple-editor-version-card__body">
                  <div className="simple-editor-version-card__content">
                    <div className="simple-editor-thread-card__summary">
                      <span className="simple-editor-version-card__heading">
                        <span className="simple-editor-version-card__title">
                          {title}
                        </span>
                        {formattedDate ? (
                          <span className="simple-editor-version-card__date">
                            {formattedDate}
                          </span>
                        ) : null}
                      </span>
                      <span className="simple-editor-version-card__chips">
                        {displayedStatus ? (
                          <Chip
                            component="span"
                            label={displayedStatus}
                            size="xxs"
                            style={
                              versionCardStatusChipStyleByStatus[
                                displayedStatus
                              ]
                            }
                            sx={versionCardChipSx}
                            variant="outlined"
                          />
                        ) : null}
                      </span>
                    </div>
                    <div className="simple-editor-version-card__details">
                      {editorNames ? (
                        <span className="simple-editor-version-card__editors">
                          {editorNames}
                        </span>
                      ) : (
                        <span className="simple-editor-version-card__editors">
                          Editor not recorded
                          <Tooltip
                            describeChild
                            title="Editor information was not recorded when this version was created."
                          >
                            <IconButton
                              aria-label="About unrecorded editor information"
                              size="small"
                              sx={{
                                ml: 0.25,
                                p: 0,
                                position: "relative",
                                zIndex: 2,
                              }}
                            >
                              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex justify-end gap-1 simple-editor-version-card__actions">
                      <Tooltip
                        title={
                          isMatchingHead
                            ? "This edit matches the current content."
                            : !isRestorable
                              ? "This version is not restorable."
                              : "Restore"
                        }
                      >
                        <span>
                          <IconButton
                            aria-label="Restore"
                            color="inherit"
                            data-version-action="restore"
                            data-version-key={versionDataKey}
                            disabled={isRestoreButtonDisabled}
                            onClick={() => onRestoreVersion(version)}
                            size="small"
                          >
                            <HistoryOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {onCompareVersion ? (
                        <VersionComparisonButton
                          comparisonOptions={comparisonOptions}
                          selectedTargetVersion={
                            isComparisonSource
                              ? (comparison?.targetVersion ?? null)
                              : null
                          }
                          isMatchingHead={isMatchingHead}
                          onCompareVersion={onCompareVersion}
                          version={version}
                          versionTitle={title}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="simple-editor-suggestions__empty">
          {getEmptyVersionsMessage(displayedFilter)}
        </p>
      )}
    </>
  ) : (
    <p className="simple-editor-suggestions__empty">No versions yet</p>
  );
};
